import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { getToken } from "../lib/auth";
import {
  LayoutDashboard, FileText, CreditCard, Truck, Building2,
  BarChart3, LogOut, Menu, X, ChevronRight, ChevronDown, ArrowLeft, Phone, MessageCircle,
  ClipboardList, Package, ShieldCheck, Archive, BookOpen, Wallet, Plug, Bell, CalendarCheck, Scale, Weight, Brain, Zap, MessageSquare, Send, History, Shield, MapPin, GitBranch, Crown, ArrowLeftRight, Lock, Banknote
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import NotificationBell from "./NotificationBell";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0d053155-ce00-4580-8f42-e450395a24b2/artifacts/1forsbvk_logo%20cmes.png";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Sidebar refactorisée — fusion des doublons :
// - Finance : Créances + Paiements reçus regroupés via onglets dans la page
// - Tournées : Expéditions + Courses + Bordereaux regroupés via onglets
// - Rapprochement : remplace Relevés bancaires + Réconciliation
// - Analytics : absorbe Rapports + IA Prédictive + Dashboard IA via onglets
// - Labels clarifiés : "Documents archivés" / "Journal d'activité"
const sidebarGroups = [
  {
    id: "main",
    items: [{ path: "/log", icon: LayoutDashboard, label: "Tableau de bord" }],
  },
  {
    id: "facturation",
    label: "Facturation",
    icon: FileText,
    items: [
      { path: "/log/invoices", icon: FileText, label: "Dossiers Factures" },
      { path: "/log/pipeline", icon: GitBranch, label: "Pipeline" },
      { path: "/log/creances", icon: Wallet, label: "Finance (Créances & Reçus)" },
      { path: "/log/pos", icon: Plug, label: "API Caisse (POS)" },
      { path: "/log/signalement", icon: Bell, label: "Signalement" },
    ],
  },
  {
    id: "logistique",
    label: "Logistique",
    icon: Truck,
    items: [
      { path: "/log/expeditions", icon: Truck, label: "Tournées (Expéditions & Courses)" },
      { path: "/log/gps", icon: MapPin, label: "Suivi GPS", roles: ["superadmin", "admin", "pharmacy", "etablissement"] },
    ],
  },
  {
    id: "assureurs",
    label: "Assureurs",
    icon: Building2,
    items: [
      { path: "/log/insurers", icon: Building2, label: "Gestion Assureurs" },
      { path: "/log/bank-statements", icon: Banknote, label: "Rapprochement bancaire" },
      { path: "/log/messagerie", icon: MessageSquare, label: "Messagerie" },
      { path: "/log/insurer-rankings", icon: Crown, label: "Classement" },
    ],
  },
  {
    id: "analyse",
    label: "Analyse & IA",
    icon: BarChart3,
    items: [
      { path: "/log/analytics", icon: BarChart3, label: "Analytics & Prédictions" },
    ],
  },
  {
    id: "general",
    items: [
      { path: "/log/archives", icon: Archive, label: "Documents archivés" },
      { path: "/log/historique", icon: History, label: "Journal d'activité" },
      { path: "/log/taches", icon: ClipboardList, label: "Tâches & Missions", roles: ["superadmin", "admin", "coursier", "agent commercial"] },
      { path: "/log/dossiers-pharmacies", icon: Shield, label: "Dossiers Pharmacies", roles: ["admin", "superadmin"] },
      { path: "/log/guide", icon: BookOpen, label: "Guide d'utilisation" },
    ],
  },
];

// Mappe les sous-routes regroupées vers leur entrée canonique du sidebar
// (utilisé pour que le menu reste actif quand on est sur un onglet de section)
const SUB_ROUTE_TO_PARENT = {
  "/log/payment-tracking": "/log/creances",
  "/log/courses": "/log/expeditions",
  "/log/bordereaux": "/log/expeditions",
  "/log/rapport-mensuel": "/log/analytics",
  "/log/ia-prédictive": "/log/analytics",
  "/log/ia-dashboard": "/log/analytics",
  "/log/reconciliation": "/log/bank-statements",
};
const matchesPath = (itemPath, currentPath) => {
  // Décode les URLs encodées (ex: /log/ia-pr%C3%A9dictive → /log/ia-prédictive)
  let decoded = currentPath;
  try { decoded = decodeURIComponent(currentPath); } catch (e) { /* ignore */ }
  return itemPath === decoded || SUB_ROUTE_TO_PARENT[decoded] === itemPath;
};

export default function LogLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openGroups, setOpenGroups] = useState(() => {
    // Auto-open the group containing the current path (ou une sous-route regroupée)
    const active = sidebarGroups.find(g => g.items?.some(i => matchesPath(i.path, location.pathname)));
    const defaults = ["main", "general"];
    if (active && !defaults.includes(active.id)) defaults.push(active.id);
    return defaults;
  });

  const toggleGroup = (id) => {
    setOpenGroups(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const fetchUnread = useCallback(() => {
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/log/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUnreadCount(r.data.unread || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    // Listen for custom event to refresh immediately
    const handler = () => fetchUnread();
    window.addEventListener("cmes-notif-read", handler);
    return () => { clearInterval(interval); window.removeEventListener("cmes-notif-read", handler); };
  }, [fetchUnread]);

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex log-dark-theme" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)' }}>
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex flex-col w-60 cmes-sidebar backdrop-blur-xl border-r border-slate-200/50 fixed h-full z-50" style={{ backgroundColor: '#0f172a' }}>
        <div className="h-16 flex items-center px-5 border-b border-white/5">
          <Link to="/hub" className="flex items-center gap-2.5 group" data-testid="log-back-hub">
            <ArrowLeft className="w-4 h-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
            <img src={LOGO_URL} alt="CMES" className="h-8 w-auto" />
            <div>
              <span className="font-bold text-blue-400 text-sm tracking-tight">CMES</span>
              <span className="block text-[9px] text-slate-500 tracking-widest font-semibold">LOG</span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {sidebarGroups.map((group) => {
            const visibleItems = group.items.filter(item => !item.roles || item.roles.includes(user?.role));
            if (visibleItems.length === 0) return null;
            const isOpen = openGroups.includes(group.id);
            const hasActiveChild = visibleItems.some(i => matchesPath(i.path, location.pathname));

            // No label = ungrouped, render items directly
            if (!group.label) {
              return visibleItems.map((item) => {
                const isActive = matchesPath(item.path, location.pathname);
                const Icon = item.icon;
                return (
                  <Link key={item.path} to={item.path}
                    data-testid={`log-nav-${item.path.split("/").pop() || "dashboard"}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      isActive ? "bg-blue-500/15 text-blue-400 font-semibold border-l-2 border-blue-500" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                    }`}>
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              });
            }

            // Grouped with collapsible header
            const GroupIcon = group.icon;
            return (
              <div key={group.id} className="mt-1">
                <button onClick={() => toggleGroup(group.id)}
                  data-testid={`log-group-${group.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                    hasActiveChild ? "text-blue-400" : "text-slate-500 hover:text-slate-300"
                  }`}>
                  <GroupIcon className="w-3.5 h-3.5" />
                  <span className="flex-1 text-left">{group.label}</span>
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
                {isOpen && (
                  <div className="ml-2 space-y-0.5 mt-0.5">
                    {visibleItems.map((item) => {
                      const isActive = matchesPath(item.path, location.pathname);
                      const Icon = item.icon;
                      return (
                        <Link key={item.path} to={item.path}
                          data-testid={`log-nav-${item.path.split("/").pop() || "dashboard"}`}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                            isActive ? "bg-blue-500/15 text-blue-400 font-semibold border-l-2 border-blue-500" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                          }`}>
                          <Icon className="w-3.5 h-3.5" />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {/* SuperAdmin-only links */}
          {user?.role === "superadmin" && (
            <>
              <div className="mt-3 mb-1 px-3 text-[10px] uppercase tracking-widest text-amber-500/50 font-bold">Administration</div>
              <Link to="/log/abonnements" data-testid="log-nav-subscriptions"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all border border-violet-500/20 ${
                  location.pathname === "/log/abonnements" ? "bg-violet-500/15 text-violet-400 font-semibold" : "text-violet-400/70 hover:bg-violet-500/10 hover:text-violet-400"
                }`}>
                <Crown className="w-4 h-4" />
                <span>Abonnements & Facturation</span>
              </Link>
              <Link to="/log/documentation" data-testid="log-nav-documentation"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all border border-blue-500/20 ${
                  location.pathname === "/log/documentation" ? "bg-blue-500/15 text-blue-400 font-semibold" : "text-blue-400/70 hover:bg-blue-500/10 hover:text-blue-400"
                }`}>
                <BookOpen className="w-4 h-4" />
                <span>Documentation technique</span>
              </Link>
              <Link to="/superadmin" data-testid="log-nav-superadmin"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mt-1 transition-all border border-amber-500/20 ${
                  location.pathname === "/superadmin" ? "bg-amber-500/15 text-amber-400 font-semibold" : "text-amber-400/70 hover:bg-amber-500/10 hover:text-amber-400"
                }`}>
                <Shield className="w-4 h-4" />
                <span>Super Administration</span>
              </Link>
            </>
          )}
          {/* Security page link (visible to all) */}
          <Link to="/securite" data-testid="log-nav-security"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mt-2 transition-all border border-emerald-500/20 ${
              location.pathname === "/securite" ? "bg-emerald-500/15 text-emerald-400 font-semibold" : "text-emerald-400/70 hover:bg-emerald-500/10 hover:text-emerald-400"
            }`}>
            <Lock className="w-4 h-4" />
            <span>S&eacute;curit&eacute; & Confidentialit&eacute;</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-white/5">
          {/* Notification Bell */}
          <Link to="/log/notifications" data-testid="log-notif-bell"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all mb-2 text-slate-400 hover:bg-white/5 hover:text-slate-200">
            <div className="relative">
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </div>
            <span>Notifications</span>
            {unreadCount > 0 && <span className="ml-auto text-[10px] text-red-400 font-medium">{unreadCount}</span>}
          </Link>
          <div className="flex items-center gap-2.5">
            <Avatar className="h-8 w-8 border border-white/10">
              <AvatarFallback className="bg-blue-500/20 text-blue-400 text-xs font-semibold">
                {user?.full_name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <Link to="/log/mon-compte" data-testid="log-my-account-link" className="text-xs font-medium text-slate-300 truncate hover:text-blue-400 block">{user?.full_name}</Link>
              <p className="text-[10px] text-slate-500 truncate">{user?.pharmacy_name || user?.role}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="log-logout-btn" className="text-slate-500 hover:text-red-400 h-7 w-7">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 cmes-sidebar backdrop-blur-md border-b border-slate-200/50 z-50 flex items-center justify-between px-4" style={{ backgroundColor: '#0f172a' }}>
        <div className="flex items-center gap-2">
          <Link to="/hub" className="text-slate-400 hover:text-blue-400"><ArrowLeft className="w-4 h-4" /></Link>
          <span className="font-bold text-blue-400 text-sm">PHARMA ASSURANCES</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 cmes-sidebar backdrop-blur-md pt-14" style={{ backgroundColor: 'rgba(15,23,42,0.98)' }}>
          <nav className="p-4 space-y-1">
            {sidebarGroups.map((group) => {
              const visibleItems = group.items.filter(item => !item.roles || item.roles.includes(user?.role));
              if (visibleItems.length === 0) return null;
              const isOpen = openGroups.includes(group.id);

              if (!group.label) {
                return visibleItems.map((item) => {
                  const isActive = matchesPath(item.path, location.pathname);
                  const Icon = item.icon;
                  return (
                    <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${isActive ? "bg-blue-500/15 text-blue-400 font-semibold" : "text-slate-400 hover:bg-white/5"}`}>
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                });
              }

              const GroupIcon = group.icon;
              return (
                <div key={group.id}>
                  <button onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <GroupIcon className="w-3.5 h-3.5" />
                    <span className="flex-1 text-left">{group.label}</span>
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  {isOpen && visibleItems.map((item) => {
                    const isActive = matchesPath(item.path, location.pathname);
                    const Icon = item.icon;
                    return (
                      <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-6 py-2.5 rounded-lg text-sm ${isActive ? "bg-blue-500/15 text-blue-400 font-semibold" : "text-slate-400 hover:bg-white/5"}`}>
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-60 pt-14 md:pt-0 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          {(title || subtitle) && (
            <div className="mb-6 flex items-start justify-between">
              <div>
                {title && <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">{title}</h1>}
                {subtitle && <p className="text-sm mt-1 text-slate-500">{subtitle}</p>}
              </div>
              <div className="hidden md:block">
                <NotificationBell />
              </div>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
